import { apiFetch } from "@/integrations/backend/client";

export type GoalMetricCatalogItem = {
  code: string;
  label: string;
  unit: string;
  supports_variant: boolean;
};

export type MetricSummary = {
  unit: string;
  start: number | null;
  start_date: string | null;
  current: number | null;
  current_date: string | null;
  delta_since_start: number | null;
  delta_since_last: number | null;
};

export type GoalsSummary = {
  resting_heart_rate: MetricSummary;
  body_weight: MetricSummary;
  rpe: MetricSummary;
  optional?: Record<string, MetricSummary>;
};

export type CreatedBy = { id: string; name: string } | null;

export type RestingHrEntry = {
  id: string;
  recorded_on: string | null;
  bpm: number;
  created_at: string | null;
  created_by: CreatedBy;
};

export type BodyWeightEntry = {
  id: string;
  recorded_on: string | null;
  weight_lbs: number;
  created_at: string | null;
  created_by: CreatedBy;
};

export type RpeEntry = {
  workout_assigned_id: string;
  date: string | null;
  workout_name: string;
  rpe: number;
  comments: string;
  created_at: string | null;
};

export type EnabledMetricsResponse = { metric_codes: string[] };

export type OptionalMetricEntry = {
  id: string;
  recorded_on: string | null;
  value: number;
  variant: number | null;
  created_at: string | null;
  created_by: CreatedBy;
};

function buildRangeQuery(from?: string, to?: string): string {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export async function getGoalsSummary(): Promise<GoalsSummary> {
  const res = await apiFetch("/api/v1/goals/summary", { method: "GET" });
  if (!res.ok) throw new Error(`goals_summary_failed:${res.status}`);
  return (await res.json()) as GoalsSummary;
}

export async function listAvailableGoalMetrics(): Promise<GoalMetricCatalogItem[]> {
  const res = await apiFetch("/api/v1/goals/available-metrics", { method: "GET" });
  if (!res.ok) throw new Error(`goals_available_metrics_failed:${res.status}`);
  return (await res.json()) as GoalMetricCatalogItem[];
}

export async function getEnabledGoalMetrics(): Promise<EnabledMetricsResponse> {
  const res = await apiFetch("/api/v1/goals/enabled-metrics", { method: "GET" });
  if (!res.ok) throw new Error(`goals_enabled_metrics_failed:${res.status}`);
  return (await res.json()) as EnabledMetricsResponse;
}

export async function listRestingHr(from?: string, to?: string): Promise<RestingHrEntry[]> {
  const res = await apiFetch(`/api/v1/goals/resting-hr${buildRangeQuery(from, to)}`, { method: "GET" });
  if (!res.ok) throw new Error(`goals_rhr_list_failed:${res.status}`);
  return (await res.json()) as RestingHrEntry[];
}

export async function createRestingHr(bpm: number, recorded_on?: string): Promise<string> {
  const res = await apiFetch("/api/v1/goals/resting-hr/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bpm, recorded_on }),
  });
  if (!res.ok) throw new Error(`goals_rhr_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

export async function listBodyWeight(from?: string, to?: string): Promise<BodyWeightEntry[]> {
  const res = await apiFetch(`/api/v1/goals/body-weight${buildRangeQuery(from, to)}`, { method: "GET" });
  if (!res.ok) throw new Error(`goals_weight_list_failed:${res.status}`);
  return (await res.json()) as BodyWeightEntry[];
}

export async function createBodyWeight(weight_lbs: number, recorded_on?: string): Promise<string> {
  const res = await apiFetch("/api/v1/goals/body-weight/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ weight_lbs, recorded_on }),
  });
  if (!res.ok) throw new Error(`goals_weight_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

export async function listRpe(from?: string, to?: string): Promise<RpeEntry[]> {
  const res = await apiFetch(`/api/v1/goals/rpe${buildRangeQuery(from, to)}`, { method: "GET" });
  if (!res.ok) throw new Error(`goals_rpe_list_failed:${res.status}`);
  return (await res.json()) as RpeEntry[];
}

export async function listOptionalMetricEntries(metricCode: string, from?: string, to?: string): Promise<OptionalMetricEntry[]> {
  const res = await apiFetch(`/api/v1/goals/metrics/${encodeURIComponent(metricCode)}${buildRangeQuery(from, to)}`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`goals_metric_list_failed:${res.status}`);
  return (await res.json()) as OptionalMetricEntry[];
}

export async function createOptionalMetricEntry(
  metricCode: string,
  value: number,
  recorded_on?: string,
  variant?: number
): Promise<string> {
  const res = await apiFetch(`/api/v1/goals/metrics/${encodeURIComponent(metricCode)}/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value, recorded_on, variant }),
  });
  if (!res.ok) throw new Error(`goals_metric_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

// Admin/trainer scoped

export async function adminGetGoalsSummary(profileId: string): Promise<GoalsSummary> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/summary`, { method: "GET" });
  if (!res.ok) throw new Error(`admin_goals_summary_failed:${res.status}`);
  return (await res.json()) as GoalsSummary;
}

export async function adminGetEnabledGoalMetrics(profileId: string): Promise<EnabledMetricsResponse> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/enabled-metrics`, { method: "GET" });
  if (!res.ok) throw new Error(`admin_goals_enabled_metrics_failed:${res.status}`);
  return (await res.json()) as EnabledMetricsResponse;
}

export async function adminSetEnabledGoalMetrics(profileId: string, enabledMetricCodes: string[]): Promise<string[]> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/enabled-metrics/set`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled_metric_codes: enabledMetricCodes }),
  });
  if (!res.ok) throw new Error(`admin_goals_set_enabled_metrics_failed:${res.status}`);
  const json = (await res.json()) as { metric_codes: string[] };
  return json.metric_codes;
}

export async function adminListRestingHr(profileId: string, from?: string, to?: string): Promise<RestingHrEntry[]> {
  const res = await apiFetch(
    `/api/v1/admin/profiles/${profileId}/goals/resting-hr${buildRangeQuery(from, to)}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`admin_goals_rhr_list_failed:${res.status}`);
  return (await res.json()) as RestingHrEntry[];
}

export async function adminCreateRestingHr(profileId: string, bpm: number, recorded_on?: string): Promise<string> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/resting-hr/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bpm, recorded_on }),
  });
  if (!res.ok) throw new Error(`admin_goals_rhr_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

export async function adminListBodyWeight(profileId: string, from?: string, to?: string): Promise<BodyWeightEntry[]> {
  const res = await apiFetch(
    `/api/v1/admin/profiles/${profileId}/goals/body-weight${buildRangeQuery(from, to)}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`admin_goals_weight_list_failed:${res.status}`);
  return (await res.json()) as BodyWeightEntry[];
}

export async function adminCreateBodyWeight(
  profileId: string,
  weight_lbs: number,
  recorded_on?: string
): Promise<string> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/body-weight/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ weight_lbs, recorded_on }),
  });
  if (!res.ok) throw new Error(`admin_goals_weight_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

export async function adminListRpe(profileId: string, from?: string, to?: string): Promise<RpeEntry[]> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/rpe${buildRangeQuery(from, to)}`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`admin_goals_rpe_list_failed:${res.status}`);
  return (await res.json()) as RpeEntry[];
}

export async function adminListOptionalMetricEntries(
  profileId: string,
  metricCode: string,
  from?: string,
  to?: string
): Promise<OptionalMetricEntry[]> {
  const res = await apiFetch(
    `/api/v1/admin/profiles/${profileId}/goals/metrics/${encodeURIComponent(metricCode)}${buildRangeQuery(from, to)}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`admin_goals_metric_list_failed:${res.status}`);
  return (await res.json()) as OptionalMetricEntry[];
}

export async function adminCreateOptionalMetricEntry(
  profileId: string,
  metricCode: string,
  value: number,
  recorded_on?: string,
  variant?: number
): Promise<string> {
  const res = await apiFetch(`/api/v1/admin/profiles/${profileId}/goals/metrics/${encodeURIComponent(metricCode)}/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value, recorded_on, variant }),
  });
  if (!res.ok) throw new Error(`admin_goals_metric_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

